import { FormEvent, useState } from 'react';
import { Button, Flex, Input, InputGroup, InputRightElement, useToast } from '@chakra-ui/react';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { adicionarProfessor } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { SENHA_MINIMA, emailInvalido, vazio } from '../../utils/validacao';
import { CampoDocumento } from '../../components/CampoDocumento';
import { TipoDocumento, erroDocumento } from '../../utils/documento';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
};

type Campo = 'nome' | 'sobrenome' | 'documento' | 'email' | 'senha';

export function DrawerCreate({ isOpen, onClose, afterClose }: DrawerCreateProps) {
	const toast = useToast();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);
	const [ mostrarSenha, setMostrarSenha ] = useState(false);

	const [ nome, setNome ] = useState<string>('');
	const [ sobrenome, setSobrenome ] = useState<string>('');
	const [ documento, setDocumento ] = useState<string>('');
	const [ tipoDocumento, setTipoDocumento ] = useState<TipoDocumento>('CPF');
	const [ email, setEmail ] = useState<string>('');
	const [ senha, setSenha ] = useState<string>('');

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome é obrigatório!';
		if (vazio(sobrenome)) encontrados.sobrenome = 'Sobrenome é obrigatório!';

		const erroDoDocumento = erroDocumento(tipoDocumento, documento);
		if (erroDoDocumento) encontrados.documento = erroDoDocumento;

		if (vazio(email)) {
			encontrados.email = 'Email é obrigatório!';
		} else if (emailInvalido(email)) {
			encontrados.email = 'Informe um email válido.';
		}

		if (!senha) {
			encontrados.senha = 'Senha é obrigatória!';
		} else if (senha.length < SENHA_MINIMA) {
			encontrados.senha = `A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`;
		}

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		adicionarProfessor({
			nome: nome.trim(),
			sobrenome: sobrenome.trim(),
			ativo: true,
			documento: documento.trim(),
			tipoDocumento,
			email: email.trim(),
			senha
		})
			.then(() => {
				setLoading(false);
				toast({
					title: `Professor(a) adicionado com sucesso.`,
					status: 'success',
					isClosable: true
				});
				resetForm();
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível adicionar professor(a).`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const resetForm = () => {
		setDocumento('');
		setTipoDocumento('CPF');
		setEmail('');
		setNome('');
		setSobrenome('');
		setSenha('');
		setMostrarSenha(false);
		limparTudo();
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<DrawerForm
			isOpen={isOpen}
			onClose={handleFechar}
			titulo="Novo Professor"
			formId="form-novo-professor"
			loading={loading}
			onSubmit={handleSalvar}
		>
			<Flex gap="16px" flexDir={{ base: 'column', sm: 'row' }}>
				<CampoForm label="Nome" htmlFor="nome" erro={erros.nome}>
					<Input
						id="nome"
						placeholder="Insira o Nome"
						autoComplete="given-name"
						onChange={(e) => {
							setNome(e.target.value);
							limparErro('nome');
						}}
						value={nome}
					/>
				</CampoForm>

				<CampoForm label="Sobrenome" htmlFor="sobrenome" erro={erros.sobrenome}>
					<Input
						id="sobrenome"
						placeholder="Insira o Sobrenome"
						autoComplete="family-name"
						onChange={(e) => {
							setSobrenome(e.target.value);
							limparErro('sobrenome');
						}}
						value={sobrenome}
					/>
				</CampoForm>
			</Flex>

			<CampoDocumento
				tipo={tipoDocumento}
				valor={documento}
				onTipoChange={(novo) => {
					setTipoDocumento(novo);
					limparErro('documento');
				}}
				onValorChange={(valor) => {
					setDocumento(valor);
					limparErro('documento');
				}}
				erro={erros.documento}
			/>

			<CampoForm label="Email" htmlFor="email" erro={erros.email}>
				<Input
					id="email"
					placeholder="professor@email.com"
					type="email"
					autoComplete="email"
					onChange={(e) => {
						setEmail(e.target.value);
						limparErro('email');
					}}
					value={email}
				/>
			</CampoForm>

			<CampoForm label="Senha" htmlFor="senha" erro={erros.senha}>
				<InputGroup size="md">
					<Input
						id="senha"
						placeholder="******"
						type={mostrarSenha ? 'text' : 'password'}
						autoComplete="new-password"
						pr="3rem"
						onChange={(e) => {
							setSenha(e.target.value);
							limparErro('senha');
						}}
						value={senha}
					/>
					<InputRightElement width="3rem">
						<Button
							h="1.75rem"
							size="sm"
							variant="unstyled"
							display="flex"
							alignItems="center"
							justifyContent="center"
							aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
							onClick={() => setMostrarSenha((atual) => !atual)}
						>
							{mostrarSenha ? <AiFillEyeInvisible size="20" /> : <AiFillEye size="20" />}
						</Button>
					</InputRightElement>
				</InputGroup>
			</CampoForm>
		</DrawerForm>
	);
}
